var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ZipAvailabilityDataSourceEnum } from '@inter-ikea-kompis/enums';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import { Hyperlink, HyperlinkColorEnum } from '@inter-ikea-kompis/component-hyperlink';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import * as SkapaStyle from '@ingka/common-styles-webc';
import { deliveryTruck, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import { Status as SkapaStatus } from '@ingka/status-webc/passive.js';
import HomeDeliveryAvailabilityEnum from '../../enums/HomeDeliveryAvailabilityEnum.js';
import HomeDeliveryLevelEnum from '../../enums/HomeDeliveryLevelEnum.js';
import HomeDeliveryUtility from '../../utilities/HomeDeliveryUtility.js';
import HomeDeliveryStyle from './HomeDeliveryStyle.js';
/**
 * @event homeDeliveryClick User clicks on the link to select zip code.
 */
export default class HomeDelivery extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Products to check home delivery status for.
        this.products = null;
        // ZIP availability object. This field should be populated together with "zipCode".
        this.zipAvailabilities = null;
        // Submitted and validated ZIP code. This field should be populated together with "zipAvailability".
        this.zipCode = null;
        this.availabilityTranslationMessage = null;
        this.dexfSettings = null;
        this.deliveryLevel = null;
    }
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        this.deliveryLevel = this.getDeliveryLevel();
        return html `
			<style>
				${HomeDeliveryStyle.getStyle({ theme: this.theme })}
			</style>

			<div class="homeDeliveryLink">${this.getStatusText()}</div>
		`;
    }
    async connectedCallback() {
        registerIcon(deliveryTruck);
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-status', SkapaStatus);
        this.customElements.define('kompis-hyperlink', Hyperlink);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation?.translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    getStatusText() {
        const deliveryTruckIcon = this.getDeliveryTruckIcon();
        const label = this.getLabel();
        const indicator = this.getIndicator(label);
        const errorHelpText = this.getErrorHelpText();
        return html `
			<div class="linkContainer ${SkapaStyle.Text.classnames({ body: { m: true } })}">
				${deliveryTruckIcon}
				<div class="availabilityContainer">${indicator} ${errorHelpText}</div>
			</div>
		`;
    }
    getDeliveryTruckIcon() {
        return html `<skapa-icon icon="delivery-truck" flipRTL></skapa-icon>`;
    }
    getErrorHelpText() {
        if (this.isEmptyZipAvailabilities()) {
            return html `<div class="info-row">
				${this.getTranslations().homeDeliveryStatusErrorHelpText}
			</div>`;
        }
        return null;
    }
    getIndicator(label) {
        const availabilityStatusOptions = {
            zipAvailabilities: this.zipAvailabilities ?? undefined,
            products: this.products ?? undefined
        };
        const availabilityStatus = HomeDeliveryUtility.getAvailabilityStatus(availabilityStatusOptions);
        const color = this.getStatusIndicatorColor(availabilityStatus);
        // Status needs to be the same element between states for focus to be put back when setting or forgetting a zip code.
        return html `
			<div class="info-row">
				<skapa-status class="${color ? '' : 'status-hidden'}" colour="${color}" position="inline"
					>${label}</skapa-status
				>
			</div>
		`;
    }
    getLabel() {
        const text = this.getLabelText();
        const link = this.getLabelLink();
        const content = this.formatLabelContent(text, link);
        return html `<span>${content}</span>`;
    }
    formatLabelContent(text, link) {
        const pattern = '[[zip_code]]';
        const phrasePattern = '{{zip_code}}';
        let start = text || '';
        let end = '';
        if (text?.includes(pattern)) {
            [start, end] = text.split(pattern);
        }
        else if (text?.includes(phrasePattern)) {
            [start, end] = text.split(phrasePattern);
        }
        return html `${start}${link}${end}`;
    }
    getLabelText() {
        if (this.zipCode && (this.isDeliveryLevelZip() || this.isDeliveryLevelUnsupported())) {
            const translation = this.getZipLevelAvailabilityTranslation();
            if (translation) {
                return translation;
            }
        }
        if (this.isDeliveryLevelMarket()) {
            return this.getMarketLevelAvailabilityTranslation();
        }
        if (this.isDeliveryLevelUnsupported()) {
            return this.getTranslations().homeDeliveryStatusUnsupported;
        }
        return null;
    }
    getLabelLink() {
        if (this.zipCode && (this.isDeliveryLevelZip() || this.isDeliveryLevelUnsupported())) {
            return this.getHyperlink(this.zipCode);
        }
        if (this.isDeliveryLevelMarket() || this.isDeliveryLevelUnsupported()) {
            return null;
        }
        return this.getHyperlink(this.getTranslations().homeDeliveryStatusInactive);
    }
    getHyperlink(label) {
        if (label) {
            return html `<kompis-hyperlink
				@linkClick="${() => this.onLinkClick()}"
				.theme="${this.theme}"
				.text="${label}"
				.color="${HyperlinkColorEnum.grey}"
				.underline="${true}"
			></kompis-hyperlink>`;
        }
        return null;
    }
    onLinkClick() {
        this.dispatchEvent(new CustomEvent('homeDeliveryClick', {
            bubbles: true
        }));
    }
    getMarketLevelAvailabilityTranslation() {
        const { homeDeliveryStatusNotSoldTemp, zipAvailabilityUnavailable, homeDeliveryStatusNotInRange } = this.getTranslations();
        const availabilityMessageType = HomeDeliveryUtility.getUnavailableStatusProducts(this.products);
        switch (availabilityMessageType) {
            case HomeDeliveryAvailabilityEnum.notInRange:
                this.availabilityTranslationMessage = homeDeliveryStatusNotInRange;
                break;
            case HomeDeliveryAvailabilityEnum.notSoldTemp:
                this.availabilityTranslationMessage = homeDeliveryStatusNotSoldTemp;
                break;
            case HomeDeliveryAvailabilityEnum.unavailable:
                this.availabilityTranslationMessage = zipAvailabilityUnavailable;
                break;
        }
        return this.availabilityTranslationMessage;
    }
    getZipLevelAvailabilityTranslation() {
        const { homeDeliveryStatusErrorMessage, homeDeliveryStatusAvailable, homeDeliveryStatusSomeAvailable, homeDeliveryStatusUnavailable, homeDeliveryStatusActive } = this.getTranslations();
        if (this.isEmptyZipAvailabilities()) {
            this.availabilityTranslationMessage = homeDeliveryStatusErrorMessage;
        }
        else if (this.zipAvailabilities) {
            const availabilityStatus = HomeDeliveryUtility.getAvailabilityStatus({
                zipAvailabilities: this.zipAvailabilities
            });
            switch (availabilityStatus) {
                case HomeDeliveryAvailabilityEnum.available:
                    this.availabilityTranslationMessage = homeDeliveryStatusAvailable;
                    break;
                case HomeDeliveryAvailabilityEnum.mixed:
                    this.availabilityTranslationMessage = homeDeliveryStatusSomeAvailable;
                    break;
                default:
                    this.availabilityTranslationMessage = homeDeliveryStatusUnavailable;
                    break;
            }
        }
        else {
            this.availabilityTranslationMessage = homeDeliveryStatusActive;
        }
        return this.availabilityTranslationMessage;
    }
    getStatusIndicatorColor(availabilityStatus) {
        if (this.isDeliveryLevelUnsupported()) {
            return 'grey';
        }
        switch (availabilityStatus) {
            case HomeDeliveryAvailabilityEnum.available:
                return 'green';
            case HomeDeliveryAvailabilityEnum.mixed:
                return 'orange';
            case HomeDeliveryAvailabilityEnum.unavailable:
                return 'red';
            case HomeDeliveryAvailabilityEnum.notInRange:
                return 'grey';
            case HomeDeliveryAvailabilityEnum.notSoldTemp:
                return 'red';
            default:
                return null;
        }
    }
    getDeliveryLevel() {
        if (!this.isHomeDeliverySupported()) {
            return HomeDeliveryLevelEnum.unsupported;
        }
        if (!!this.products &&
            this.products.length > 0 &&
            this.isAnyProductUnavailableForHomeDelivery(this.products)) {
            return HomeDeliveryLevelEnum.market;
        }
        return HomeDeliveryLevelEnum.zip;
    }
    isHomeDeliverySupported() {
        const zipAvailabilityDataSourceEnabled = this.getSettings().kompis.serviceSettings.zipAvailabilityDataSource !==
            ZipAvailabilityDataSourceEnum.disabled;
        return !this.isEmptyZipAvailabilities() && zipAvailabilityDataSourceEnabled;
    }
    isAnyProductUnavailableForHomeDelivery(products) {
        return products.some((product) => product?.content?.priceInformation?.availableForHomeDelivery === false);
    }
    isEmptyZipAvailabilities() {
        return Array.isArray(this.zipAvailabilities) && this.zipAvailabilities.length === 0;
    }
    isDeliveryLevelMarket() {
        return this.deliveryLevel === HomeDeliveryLevelEnum.market;
    }
    isDeliveryLevelZip() {
        return this.deliveryLevel === HomeDeliveryLevelEnum.zip;
    }
    isDeliveryLevelUnsupported() {
        return this.deliveryLevel === HomeDeliveryLevelEnum.unsupported;
    }
}
__decorate([
    property({ type: Object, required: true })
], HomeDelivery.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], HomeDelivery.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], HomeDelivery.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], HomeDelivery.prototype, "settings", void 0);
__decorate([
    property({ type: Object })
], HomeDelivery.prototype, "products", void 0);
__decorate([
    property({ type: Object })
], HomeDelivery.prototype, "zipAvailabilities", void 0);
__decorate([
    property({ type: String })
], HomeDelivery.prototype, "zipCode", void 0);
//# sourceMappingURL=HomeDelivery.js.map